import { Action } from '@/_core/fluxstore/types';
import orderBy from 'lodash/orderBy';
import { Activity, StorymapState, Subtask, Task, ViewType } from '../../types';
import * as smUse from '../../use';
import { useStorymapStore } from '../../useModule';
import { getVisibleActivitiesForNav, getVisibleTasksForNav } from '../../use/useHideUnfiltered';
import { getFirstTrackingVisibleCard, getTrackingVisibleStatusColumns, getTrackingVisibleCardPosition } from '../../use/useTrackingVisibleCards';

const { registerReducer } = useStorymapStore();

export class SelectionMoveRight extends Action<StorymapState> {
    public static ACTION_TYPE = 'SelectionMoveRight';

    public constructor() {
        super(SelectionMoveRight.ACTION_TYPE);
    }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const reducer = (state: StorymapState, action: SelectionMoveRight): void => {
    if (state.currentViewType === ViewType.Tracking) {
        moveTrackingSelectionRight(state);
        return;
    }

    const card = smUse.getActiveCard();
    if (!card) {
        const firstActivity = orderBy(state.activities, 'order').shift();
        firstActivity.isActive = true;
        return;
    }

    const cardType = smUse.getCardType(card);
    switch (cardType) {
        case smUse.CardType.Activity:
            moveActivitySelection(card as Activity, state);
            break;
        case smUse.CardType.Task:
            moveTaskSelection(card as Task, state);
            break;
        case smUse.CardType.Subtask:
            state.navigationState.shouldKeepOriginalCardRectOnSelectionChange = true;
            moveSubtaskSelection(card as Subtask, state);
            break;
    }
};

const moveTrackingSelectionRight = (state: StorymapState) => {
    const releaseId = state.trackingViewState.releaseId;
    if (!releaseId) {
        return;
    }

    const currentCard = smUse.getActiveCard();
    const firstCard = getFirstTrackingVisibleCard(state, releaseId);

    if (!firstCard) {
        return;
    }

    if (!smUse.isSubtask(currentCard) || !getTrackingVisibleCardPosition(state, currentCard.id, releaseId)) {
        firstCard.isActive = true;
        currentCard && (currentCard.isActive = false);
        state.navigationState.originalSubtask = null;
        state.navigationState.shouldKeepOriginalCardRectOnSelectionChange = false;
        return;
    }

    const currentPosition = getTrackingVisibleCardPosition(state, currentCard.id, releaseId);
    if (!currentPosition) {
        return;
    }

    const nextColumn = getTrackingVisibleStatusColumns(state, releaseId)
        .slice(currentPosition.columnIndex + 1)
        .find((column) => column.cards.length > 0);

    if (!nextColumn) {
        return;
    }

    nextColumn.cards[0].isActive = true;
    currentCard.isActive = false;
    state.navigationState.originalSubtask = null;
    state.navigationState.shouldKeepOriginalCardRectOnSelectionChange = false;
};

const moveActivitySelection = (current: Activity, state: StorymapState) => {
    const activities = getVisibleActivitiesForNav(state);

    const next = orderBy(
        activities.filter((x) => x.order > current.order),
        'order'
    ).shift();

    if (next) {
        next.isActive = true;
        current.isActive = false;
    }
};

const moveTaskSelection = (current: Task, state: StorymapState) => {
    const activities = getVisibleActivitiesForNav(state);
    const tasks = getVisibleTasksForNav(state);

    let next: Task | Activity = orderBy(
        tasks.filter((x) => x.activityId == current.activityId && x.order > current.order),
        'order'
    ).shift();

    if (!next) {
        const currentActivity = activities.find((x) => x.id == current.activityId);
        const nextActivity = orderBy(
            activities.filter((x) => x.order > currentActivity.order),
            'order'
        ).shift();

        if (nextActivity) {
            next = orderBy(
                tasks.filter((x) => x.activityId == nextActivity.id),
                'order'
            ).shift();

            if (!next) {
                next = nextActivity;
            }
        }
    }

    if (next) {
        next.isActive = true;
        current.isActive = false;
    }
};

const moveSubtaskSelection = (current: Subtask, state: StorymapState) => {
    const currentActivity = state.activities.find((x) => x.id == current.activityId);
    const currentTask = state.tasks.find((x) => x.id == current.taskId);

    const visibleTasks = getVisibleTasksForNav(state);

    let mappedTasks = visibleTasks.map((x) => {
        const activity = state.activities.find((a) => a.id == x.activityId);
        return {
            activityOrder: activity.order,
            ...x,
        };
    });

    mappedTasks = mappedTasks.filter((x) => {
        if (x.activityId == currentActivity.id) {
            return x.order > currentTask.order;
        }

        return x.activityOrder > currentActivity.order;
    });

    const nextTasks = orderBy(mappedTasks, ['activityOrder', 'order']);

    smUse.moveSubtaskInActivity(current, nextTasks, state);
};

//@ts-ignore
registerReducer(SelectionMoveRight.ACTION_TYPE, reducer);
